<script setup lang="ts">
import { WarningFilled } from "@element-plus/icons-vue";
import { ElMessage, ElMessageBox } from "element-plus";
import { computed, onMounted, onUnmounted, provide, ref, watch } from "vue";
import type {
  FormDefinition,
  FormElementBase,
  FormResponse,
  FormResponseStatus,
  ListDefinition,
  Workflow,
} from "~/api";
import { FormConcurrencyMode } from "~/api";
import { useLiveFormResponseManager } from "~/NewFormBuilder/composables/useLiveFormResponseManager";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import {
  FormPage,
  formResponseInjectKey,
  FormResponseState,
  useFormResponse,
} from "../../composables/useFormResponse";
import { apiClient } from "~/utils/apiClient";
import { emptyGuid } from "~/utils/constants";
import { FormDisplayMode } from "../../types/FormDisplayMode";
import TableOfContents from "../Designer/TableOfContents.vue";
import FormComponentRenderer from "../FormComponents/FormComponentRenderer.vue";
import { formActivityInjectKey } from "./formActivity";

const props = withDefaults(
  defineProps<{
    formResponse?: FormResponse;
    /** Whether to split form pages. */
    splitPages?: boolean;
    /** Whether to show a JSON dump of the current answers. */
    showAnswers?: boolean;
    /** Whether to show the table of contents. */
    showToc?: boolean;
    /** Whether the form is in view only mode */
    displayMode?: FormDisplayMode;
    /** Whether to disable the form's rules engine */
    disableRulesEngine?: boolean;
    /** Whether to scroll to a specific form section */
    scrollToQuestionCode?: string;
    /** workflow */
    workflow?: Workflow;

    pdfViewer?: boolean;
    /**
     * True when this response is a superseded (previous) version, decided by the parent viewer's
     * version grouping. Drives the "Previous version" banner so it always matches the version the
     * UI shows.
     */
    isPreviousVersion?: boolean;
    /** Answers from all forms that are marked for sharing */
    sharedAnswers?: Record<string, string>;
    /** Optional callback to trigger workflow refresh after status changes */
    onWorkflowRefresh?: () => Promise<void>;
  }>(),
  {
    formResponse: undefined,
    splitPages: true,
    showAnswers: false,
    showToc: false,
    displayMode: FormDisplayMode.EDIT,
    disableRulesEngine: false,
    scrollToQuestionCode: undefined,
    workflow: undefined,
    pdfViewer: false,
    isPreviousVersion: false,
    sharedAnswers: undefined,
    onWorkflowRefresh: undefined,
  },
);

const emit = defineEmits<{
  (e: "update:formStatus", status: FormResponseStatus): void;
  (e: "update:formPages", value: FormPage[]): void;
  (e: "update:unansweredQuestions", unanswered: FormElementBase[]): void;
}>();

const formDefinition = defineModel<FormDefinition>({ required: true });

defineSlots<{
  component(props: { component: FormElementBase }): any;
  default(props: { formResponseState: FormResponseState }): any;
}>();

const listDefinitions = ref<ListDefinition[]>([]);

const formResponseState = useFormResponse({
  formDefinition: formDefinition.value,
  formResponse: props.formResponse,
  displayMode: props.displayMode,
  disableRulesEngine: props.disableRulesEngine,
  workflow: props.workflow,
  sharedAnswers: props.sharedAnswers,
  onWorkflowRefresh: props.onWorkflowRefresh,
  listDefinitions,
});

const myInfoStore = useMyInfoStore();

const {
  startConnection,
  viewerResponseMap,
  formResponseEditors,
  onFormUpdated,
  onControlRequestReceived,
  onControlRequestResponse,
  requestFormControl,
  respondToControlRequest,
  reportIdle,
  reportActive,
} = useLiveFormResponseManager();

const controlRequestPending = ref(false);
const isCurrentUserIdle = ref(false);
const formHostRef = ref<HTMLElement>();
let idleTimer: ReturnType<typeof setTimeout> | null = null;
let lastActivityReset = 0;

const concurrencyMode = computed(
  () =>
    formDefinition.value.metaInfo.concurrencyMode ??
    FormConcurrencyMode.FIRST_IN_WINS,
);

/** Whether idle timeout applies to the current concurrency mode */
const idleTimeoutApplies = computed(
  () =>
    concurrencyMode.value === FormConcurrencyMode.FIRST_IN_WINS ||
    concurrencyMode.value === FormConcurrencyMode.REQUEST_CONTROL,
);

const idleTimeoutMs = computed(
  () => (formDefinition.value.metaInfo.idleTimeoutMinutes ?? 5) * 60 * 1000,
);

const resetIdleTimer = () => {
  // Throttle resets to once per second
  const now = Date.now();
  if (now - lastActivityReset < 1000) return;
  lastActivityReset = now;

  if (idleTimer) clearTimeout(idleTimer);
  idleTimer = setTimeout(() => {
    if (props.formResponse && idleTimeoutApplies.value) {
      isCurrentUserIdle.value = true;
      reportIdle(props.formResponse.guid);
    }
  }, idleTimeoutMs.value);
};

/** Can only resume if no other user is actively (non-idle) viewing the form */
const canResumeEditing = computed(() => {
  const editors = formResponseEditors.value;
  const currentUserName = myInfoStore.userInfo?.loginName;
  const activeOthers = editors.filter(
    (u) => u.userName !== currentUserName && !u.isIdle,
  );
  return activeOthers.length === 0;
});

const resumeEditing = () => {
  if (!props.formResponse || !isCurrentUserIdle.value) return;
  if (!canResumeEditing.value) return;
  isCurrentUserIdle.value = false;
  reportActive(props.formResponse.guid);
  resetIdleTimer();
};

const handleFormActivity = () => {
  if (isCurrentUserIdle.value) {
    resumeEditing();
  } else {
    resetIdleTimer();
  }
};

// Expose activity reporting to descendant components whose editable surface lives
// in an iframe (e.g. TinyMCE RichText), where keystrokes never reach the
// @keydown.capture handler on .formHost.
provide(formActivityInjectKey, handleFormActivity);

/** Effective display mode: most restrictive of parent prop and concurrency mode */
const effectiveDisplayMode = computed(() => {
  // If parent already says read-only, respect that unconditionally
  if (props.displayMode === FormDisplayMode.READ_ONLY) {
    return FormDisplayMode.READ_ONLY;
  }

  // If current user is idle, force read-only
  if (isCurrentUserIdle.value && idleTimeoutApplies.value) {
    return FormDisplayMode.READ_ONLY;
  }

  const editors = formResponseEditors.value;
  const currentUserName = myInfoStore.userInfo?.loginName;
  // Filter out idle users — they don't block others from editing
  const otherUsers = editors.filter(
    (u) => u.userName !== currentUserName && !u.isIdle,
  );

  // No other active users — always allow editing
  if (otherUsers.length === 0) {
    return FormDisplayMode.EDIT;
  }

  switch (concurrencyMode.value) {
    case FormConcurrencyMode.READ_ONLY_WHEN_SHARED:
      return FormDisplayMode.READ_ONLY;

    case FormConcurrencyMode.ALLOW_CONCURRENT:
      return FormDisplayMode.EDIT;

    case FormConcurrencyMode.REQUEST_CONTROL:
    case FormConcurrencyMode.FIRST_IN_WINS:
    default: {
      // First-in-wins: earliest non-idle user gets edit access
      const currentUser = editors.find((u) => u.userName === currentUserName);
      if (!currentUser) return FormDisplayMode.READ_ONLY;

      const currentJoined = new Date(currentUser.joinedAt);
      const isEarliest = otherUsers.every(
        (u) => !u.joinedAt || new Date(u.joinedAt) > currentJoined,
      );
      return isEarliest ? FormDisplayMode.EDIT : FormDisplayMode.READ_ONLY;
    }
  }
});

/** Name of the user currently editing, if someone else holds edit control */
const currentEditorName = computed(() => {
  // Only relevant when concurrency forced us into read-only
  if (
    effectiveDisplayMode.value !== FormDisplayMode.READ_ONLY ||
    props.displayMode === FormDisplayMode.READ_ONLY
  ) {
    return null;
  }

  const editors = formResponseEditors.value;
  const currentUserName = myInfoStore.userInfo?.loginName;
  const activeOthers = editors.filter(
    (u) => u.userName !== currentUserName && !u.isIdle,
  );

  if (activeOthers.length === 0) return null;

  switch (concurrencyMode.value) {
    case FormConcurrencyMode.FIRST_IN_WINS:
    case FormConcurrencyMode.REQUEST_CONTROL: {
      // Editor is the earliest non-idle user
      const sorted = [...activeOthers].sort(
        (a, b) =>
          new Date(a.joinedAt).getTime() - new Date(b.joinedAt).getTime(),
      );
      return sorted[0]?.userName ?? null;
    }
    case FormConcurrencyMode.READ_ONLY_WHEN_SHARED:
      return null; // everyone is read-only, no single editor
    default:
      return null;
  }
});

/** Whether to show the Request Control button */
const showRequestControl = computed(
  () =>
    concurrencyMode.value === FormConcurrencyMode.REQUEST_CONTROL &&
    effectiveDisplayMode.value === FormDisplayMode.READ_ONLY &&
    props.displayMode !== FormDisplayMode.READ_ONLY,
);

/** The current form page number we are looking at */
const currentPageNumber = defineModel<number>("pageNumber", {
  required: false,
  default: 1,
});
/** The current form page we are looking at */
const currentPage = computed(
  () => formResponseState.pages.value[currentPageNumber.value - 1],
);
/** The components of the current form page we are looking at, or all the components if we are not splitting pages */
const currentComponents = computed(() =>
  props.splitPages
    ? currentPage.value.components
    : formDefinition.value.components,
);
/** The guids of the sections in the form */
const sectionGuids = computed(() =>
  formDefinition.value.components
    .filter((c) => c.type === "Section")
    .map((c) => c.guid),
);
/** The people currently viewing the form */
const viewers = computed(() => {
  if (!props.formResponse) {
    return [];
  }
  return viewerResponseMap.value[props.formResponse.guid] ?? [];
});
const otherViewers = computed(() => {
  const currentUserName = myInfoStore.userInfo?.loginName;
  return viewers.value.filter((v) => v !== currentUserName);
});
// provide form state to child components
provide(formResponseInjectKey, formResponseState);

// make sure we don't stay on a page that just got removed (from the FormDesigner)
watch(
  () => formResponseState.pages.value.length,
  (newLength) => {
    if (newLength < currentPageNumber.value) {
      // if we were, just go to the last page based on the new length
      currentPageNumber.value = newLength;
    }
  },
);
watch(
  () => formResponseState.formResponse?.status,
  (newStatus) => {
    if (newStatus) {
      emit("update:formStatus", newStatus);
    }
  },
);

watch(
  () => formResponseState.getUnansweredQuestions(),
  (newVal) => {
    emit("update:unansweredQuestions", newVal);
  },
  { immediate: true, deep: true },
);

watch(
  () => formResponseState.pages.value,
  () => emit("update:formPages", formResponseState.pages.value),
  { immediate: true },
);

onMounted(async () => {
  // Fetch list definitions for use in mustache showLabelsFromList substitution.
  // Errors are intentionally ignored – the feature degrades gracefully without lists.
  try {
    const workflowDefGuid =
      props.workflow?.workflowDefinition?.guid ?? emptyGuid;
    listDefinitions.value =
      await apiClient.listDefinition.getAllListDefinitions(
        formDefinition.value.guid,
        workflowDefGuid,
        myInfoStore.currentSysOrgGuid,
      );
  } catch {
    // list definitions unavailable – showLabelsFromList will use its fallback
  }

  if (props.scrollToQuestionCode) {
    const formSection = document.querySelector(
      `[question-code="${props.scrollToQuestionCode}"]`,
    );
    if (formSection) {
      formSection.scrollIntoView({ behavior: "smooth" });
    }
  }

  if (props.formResponse && !props.pdfViewer) {
    await startConnection(props.formResponse.guid);

    // Start idle timer for applicable concurrency modes
    if (idleTimeoutApplies.value) {
      resetIdleTimer();
      // Scrolling (wheel/touchpad/scrollbar) is real activity but doesn't fire
      // mousedown/keydown, and scroll happens on an ancestor scroll container —
      // not .formHost — so listen at the document level in the capture phase.
      document.addEventListener("wheel", handleFormActivity, {
        capture: true,
        passive: true,
      });
      document.addEventListener("scroll", handleFormActivity, {
        capture: true,
        passive: true,
      });
    }

    onFormUpdated(async () => {
      await formResponseState.refreshRecentlyUpdatedAnswers();
    });

    onControlRequestReceived(async (request: any) => {
      try {
        await ElMessageBox.confirm(
          `${request.requestingUserName} is requesting edit control. Grant access?`,
          "Control Request",
          {
            confirmButtonText: "Grant",
            cancelButtonText: "Deny",
            type: "warning",
          },
        );
        await respondToControlRequest(
          props.formResponse!.guid,
          request.requestingConnectionId,
          true,
        );
      } catch {
        await respondToControlRequest(
          props.formResponse!.guid,
          request.requestingConnectionId,
          false,
        );
      }
    });

    onControlRequestResponse((response: any) => {
      controlRequestPending.value = false;
      if (response.approved) {
        ElMessage.success("Control granted! You can now edit.");
      } else {
        ElMessage.info(`${response.fromUserName} denied your control request.`);
      }
    });
  }
});

onUnmounted(() => {
  document.removeEventListener("wheel", handleFormActivity, true);
  document.removeEventListener("scroll", handleFormActivity, true);
  if (idleTimer) clearTimeout(idleTimer);
});
</script>

<template>
  <div v-if="isPreviousVersion" class="viewingOriginalBanner">
    <el-alert
      title="Previous version — read-only, showing the answers captured on this version"
      type="info"
      center
      :closable="false"
    />
  </div>
  <div v-if="currentEditorName" class="editorBanner">
    <el-alert
      :title="`Currently being edited by ${currentEditorName}`"
      type="warning"
      center
      class="editorsBar"
      :closable="false"
    >
      <template v-if="showRequestControl" #default>
        <el-button
          :loading="controlRequestPending"
          type="warning"
          size="small"
          @click="
            controlRequestPending = true;
            requestFormControl(formResponse!.guid);
          "
        >
          Request Control
        </el-button>
      </template>
    </el-alert>
  </div>
  <div v-else-if="viewers.length > 1" class="viewersBar">
    <span>Currently being viewed by:</span>
    <el-alert
      v-for="viewer in otherViewers"
      :key="viewer"
      :title="viewer"
      type="info"
      center
      :closable="false"
    />
  </div>
  <Teleport to="body">
    <div v-if="isCurrentUserIdle && idleTimeoutApplies" class="idleOverlay">
      <div class="idleCard" role="status" aria-live="polite" aria-atomic="true">
        <el-icon class="idleCard__icon" aria-hidden="true"
          ><WarningFilled
        /></el-icon>
        <template v-if="canResumeEditing">
          <p class="idleCard__title">You've been idle</p>
          <p class="idleCard__text">
            This form was switched to read-only to avoid conflicting edits.
          </p>
          <el-button type="primary" @click="resumeEditing">
            Resume Editing
          </el-button>
        </template>
        <template v-else>
          <p class="idleCard__title">You've been idle</p>
          <p class="idleCard__text">Another user is now editing this form.</p>
        </template>
      </div>
    </div>
  </Teleport>
  <div
    v-if="formDefinition"
    ref="formHostRef"
    class="formHost"
    @mousedown.capture="handleFormActivity"
    @mousemove.capture="handleFormActivity"
    @keydown.capture="handleFormActivity"
  >
    <div v-if="showToc" class="table-of-contents">
      <TableOfContents :form="formDefinition" />
    </div>
    <!-- slot for adding custom component rendering -->
    <div class="form">
      <slot
        v-for="component in currentComponents.filter(
          (c) =>
            sectionGuids.includes(c.parentGuid) ||
            c.parentGuid == formDefinition.guid,
        )"
        name="component"
        :component="component"
      >
        <FormComponentRenderer
          :model-value="component"
          :display-mode="effectiveDisplayMode"
          :question-code="component.questionCode"
        />
      </slot>
    </div>

    <div v-if="showAnswers" class="formAnswers">
      <h3>Form Answers</h3>
      {{ JSON.stringify(formResponseState.answers, null, 2) }}
    </div>

    <el-pagination
      v-if="splitPages"
      v-model:current-page="currentPageNumber"
      class="formPagination"
      layout="prev, pager, next"
      :page-size="1"
      :total="formResponseState.pages.value.length"
      size="large"
      hide-on-single-page
    />
  </div>
</template>

<style lang="less">
.idleOverlay {
  // Centered, viewport-fixed so the read-only notice is always visible no matter
  // where the user has scrolled. Non-blocking: clicks pass through everywhere
  // except the card itself, so the form stays readable/scrollable.
  position: fixed;
  inset: 0;
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.idleCard {
  pointer-events: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5em;
  width: fit-content;
  max-width: min(90vw, 420px);
  padding: 2em 2.5em;
  text-align: center;
  background: var(--el-bg-color, #fff);
  border: 1px solid var(--el-border-color-light);
  border-radius: 12px;
  box-shadow: var(--el-box-shadow-dark);

  &__icon {
    font-size: 2.75rem;
    color: var(--el-color-warning);
  }

  &__title {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--el-text-color-primary);
  }

  &__text {
    margin: 0 0 0.5em;
    color: var(--el-text-color-regular);
  }
}
.editorBanner {
  position: sticky;
  top: 0;
  z-index: 10;
  padding-bottom: 1em;

  .el-button {
    margin-left: 1em;
  }
}
.editorsBar {
  width: fit-content;
  margin: 0 auto;
  font-size: 1rem;
}
.viewersBar {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5em;
  position: sticky;
  top: 0;
  z-index: 10;
  padding-bottom: 1em;

  .el-alert {
    width: fit-content;
  }
}
.formHost {
  display: grid;
  grid-template-columns: auto minmax(auto, 1200px) auto;
  grid-template-areas:
    "toc form answers"
    "toc pages answers";
  row-gap: 1em;
  margin: 0 auto;

  .form {
    grid-area: form;
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 8px 8px 8px 16px;
    border: 2px solid #bbb;
    border-radius: var(--el-border-radius-base);
  }

  .table-of-contents {
    grid-area: toc;
    width: 250px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .formAnswers {
    grid-area: answers;
    min-width: 500px;
    padding: 4px 8px;
    white-space: break-spaces;
  }

  .formPagination {
    grid-area: pages;
    justify-content: center;
  }

  h1 {
    font-size: 1.5em;
    margin-bottom: 1em;
    font-weight: normal;
  }
  h2 {
    font-size: 1.2em;
    margin-bottom: 1em;
    font-weight: normal;
  }
  h3 {
    font-size: 1em;
    margin-bottom: 1em;
    font-weight: normal;
  }

  --form-input-text-color: var(--el-text-color-primary);
  --el-border-color: var(--el-text-color-secondary);
  --el-input-border-color: var(--el-border-color);
  --el-border-width: 2px;

  --el-border: var(--el-border-width) var(--el-border-style)
    var(--el-border-color);

  .el-input__wrapper,
  .el-select__wrapper,
  .el-select__wrapper.is-hovering:not(.is-focused) {
    box-shadow: 0 0 0px 2px var(--el-input-border-color, var(--el-border-color))
      inset;
  }

  .el-input__inner {
    color: var(--form-input-text-color, var(--el-text-color-regular));
  }

  .el-input-group__prepend {
    box-shadow: none;
    color: var(--el-font-color, var(--form-input-text-color));
  }
  .el-checkbox {
    --el-checkbox-input-height: 1em;
    --el-checkbox-input-width: 1em;
    --el-checkbox-text-color: var(--form-input-text-color);
  }

  .el-radio__inner,
  .el-checkbox__inner {
    border: 2px solid var(--el-border-color);
  }

  .el-select__wrapper {
    font-size: var(--el-font-size-base);
    color: var(--form-input-text-color);

    .el-select__placeholder {
      color: var(--form-input-text-color);
      &.is-transparent {
        color: var(--el-text-color-placeholder);
      }
    }
  }

  --toolbar-color: var(--el-bg-color-page);

  .tox-tinymce {
    border: 2px solid var(--el-border-color);
    .tox-editor-header,
    .tox-toolbar {
      background-color: var(--toolbar-color);
    }
    &.tox {
      .tox-editor-header {
        background-color: var(--toolbar-color);
        box-shadow: none;
        padding: 0;
      }
      .tox-toolbar__group {
        background-color: var(--toolbar-color);
      }
      .tox-tbtn {
        background: var(--toolbar-color);
        color: var(--form-input-text-color);
        margin: 0;
        height: auto;
        &:active {
          background: #a6ccf7;
          color: #222f3e;
        }
      }
    }
  }

  /** standardize input widths */
  --standard-input-width: 280px;
  .el-select {
    --el-select-width: var(--standard-input-width);
  }

  .el-input-width {
    --el-input-width: auto;
  }

  .el-date-editor,
  .el-input-number,
  .el-input {
    width: auto;
  }

  .el-input__wrapper {
    width: calc(var(--standard-input-width)); // minus the padding
    // width: calc(var(--standard-input-width) - 22px); // minus the padding
    @media (max-width: 500px) {
      width: calc(var(--standard-input-width) - 110px);
    }
  }

  .el-input-number {
    .el-input__wrapper {
      width: calc(var(--standard-input-width) - 22px); // minus the padding
    }
  }
}
</style>
